import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

export default function PasswordReset() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/password-reset/verify/${token}`);
        setValid(true);
        setEmail(data.email || "");
      } catch (e) {
        const detail = e?.response?.data?.detail || "Reset link invalid or expired";
        setError(typeof detail === "string" ? detail : detail?.message || "Invalid token");
      } finally {
        setVerifying(false);
      }
    })();
  }, [token]);

  const onSubmit = async () => {
    if (newPwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: newPwd });
      setDone(true);
      toast.success("Password reset — please sign in");
      setTimeout(() => navigate("/login"), 1800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="password-reset-page">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="text-sm font-bold tracking-wider text-slate-900 mb-2">
            OPSTR<span className="text-[#0066ff]">AI</span>
          </div>
          <CardTitle className="text-xl">Reset password</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {verifying ? (
            <p className="text-sm text-slate-500">Verifying link…</p>
          ) : !valid ? (
            <div data-testid="reset-invalid" className="space-y-3">
              <p className="text-sm text-rose-700">{error}</p>
              <Button variant="outline" onClick={() => navigate("/login")}>Back to sign in</Button>
            </div>
          ) : done ? (
            <p data-testid="reset-done" className="text-sm text-emerald-700">Password reset. Redirecting to sign in…</p>
          ) : (
            <>
              <div className="text-sm text-slate-600">Account: <span className="font-medium text-slate-900">{email}</span></div>
              <div>
                <Label htmlFor="new-pwd">New password</Label>
                <Input data-testid="reset-new-password-input" id="new-pwd" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="mt-1" />
                <p className="text-xs text-slate-500 mt-1">Minimum 8 characters.</p>
              </div>
              <Button data-testid="reset-submit-btn" onClick={onSubmit} disabled={submitting} className="w-full bg-[#0066ff] hover:bg-[#0052cc]">
                {submitting ? "Setting password…" : "Set new password"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
